// NavigationBar.tsx
import * as React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
} from "@mui/material";
import { supabase } from "../logic/client";
import { useI18n } from "../i18n/I18nProvider";
import { CompanyRole, loadCachedRole, saveCachedRole } from "../logic/cache";


type NavProps = {
  currentPage: string;
  handlePageChange: (page: string) => void;
  onHelpClick?: () => void;
};

export default function NavigationBar({
  currentPage,
  handlePageChange,
}: NavProps) {
  const { lang, setLang, t } = useI18n();

  // tablet menu
  const bg = "linear-gradient(180deg, rgba(114, 0, 6, 0.9) 0%, rgba(180,20,30,0.9) 100%)";

  /* ---------------- AUTHZ: load role ---------------- */
  const cachedRole = React.useMemo(() => loadCachedRole(), []);
  const [, setRole] = React.useState<CompanyRole>(cachedRole ?? "employee");
  const [, setRoleLoading] = React.useState(!cachedRole);

  React.useEffect(() => {
    async function loadRole() {
      setRoleLoading(true);
      try {
        const { data: sess } = await supabase.auth.getSession();
        const session = sess.session;

        // No session -> public state, don't query DB
        if (!session?.user?.id) {
          setRole("employee");
          return;
        }

        const userId = session.user.id;

        const { data: emp, error } = await supabase
          .from("employees")
          .select("company_role")
          .eq("auth_user_id", userId)
          .maybeSingle();

        if (error) {
          setRole("employee");
          return;
        }

        const nextRole = (emp?.company_role as CompanyRole) ?? "employee";
        setRole(nextRole);
        saveCachedRole(nextRole);
      } finally {
        setRoleLoading(false);
      }
    }

    loadRole();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      loadRole();
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  return (
  <AppBar
    position="static"
    elevation={0}
    sx={{
      height: 68,
      justifyContent: "center",
      background: bg,
      borderBottom: "1px solid #00000014",
    }}
  >
    <Toolbar
      disableGutters
      sx={{
        minHeight: 68,
        px: { xs: 1.5, sm: 2.5 },
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* LEFT: Logo */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src="/logo.png"
          alt="takeKONTROL"
          sx={{ height: { xs: 32, sm: 40 }, width: "auto" }}
        />
      </Box>

      {/* CENTER: controller label */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Typography
          noWrap
          sx={{
            fontWeight: 900,
            letterSpacing: { xs: "0.02em", sm: "0.04em" },
            color: "#ffffff",
            fontSize: { xs: 14, sm: 18, md: 20 },
            textAlign: "center",
          }}
        >
          CONTROL PANEL
        </Typography>
      </Box>

      {/* RIGHT: language + hamburger */}
      <Stack direction="row" spacing={1} alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={lang}
          onChange={(_, next) => {
            if (next) setLang(next);
          }}
          sx={{
            bgcolor: "#ffe9e9cb",
            border: "1px solid #00000014",
            borderRadius: 999,
            overflow: "hidden",
            "& .MuiToggleButton-root": {
              px: 1.1,
              py: 0.45,
              fontWeight: 900,
              textTransform: "none",
              border: 0,
              color: "#111827B3",
            },
            "& .Mui-selected": {
              color: "#111827",
              bgcolor: "#11182712",
            },
          }}
        >
          <ToggleButton value="en">{t("lang.en")}</ToggleButton>
          <ToggleButton value="de">{t("lang.de")}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Toolbar>
  </AppBar>
);
}
